<template>
  <v-navigation-drawer
    app
    clipped
    :value="value"
    width="290"
    @input="$emit('input', $event)"
  >
    <SeoScore
      v-if="showSeoScore"
      :context="seoScoreContext"
    />
    <LoginMenuTeaser v-else-if="!Auth.isAuthenticated && !isShareRoute" />
    <ListOfFeatures
      v-else-if="!isShareRoute"
      :article-step="articleStep"
      :is-in-article-detail="isOwnerArticle"
    />
    <div
      v-if="isShareRoute"
      class="share-drawer-cta"
    >
      <ArticleKeywords />
      <p class="share-drawer-cta__text mt-4">{{ $t('share.drawerHint') }}</p>
      <v-btn
        block
        depressed
        color="primary"
        class="text-none"
        @click="goRegister"
      >
        {{ $store.getters.authenticated ? $t('share.openOwn') : $t('share.register') }}
      </v-btn>
    </div>
    <div v-if="isOwnerArticle">
      <div
        class="d-flex flex-column align-stretch mt-6 mx-auto"
        style="max-width: 95%; row-gap: 16px; width: 95%"
      >
        <ArticleKeywords />
        <ArticleSaveButton
          block
          :disabled="disableSave"
          @input="saveArticle"
        />
        <ArticlePreview block />
        <ArticleShareButton block />
        <ArticleNewInspirationButton block />
        <ArticleNewInspirationButton
          block
          is-analyze
        />
      </div>
      <ArticleSimpleList />
    </div>
  </v-navigation-drawer>
</template>

<script lang="ts">
    import {EventBus} from "@/utils/EventBus";
    import Vue, {PropType} from "vue";
    import ArticleNewInspirationButton from "@/components/article/ArticleNewInspirationButton.vue";
    import ArticlePreview from "@/components/article/ArticlePreview.vue";
    import ArticleSaveButton from "@/components/article/ArticleSaveButton.vue";
    import ArticleSimpleList from "@/components/article/ArticleSimpleList.vue";
    import {Auth} from "@/plugins/Auth";
    import ListOfFeatures from "@/components/ListOfFeatures.vue";
    import LoginMenuTeaser from "@/components/LoginMenuTeaser.vue";
    import SeoScore from "@/components/seoscore/SeoScore.vue";
    import {seoScoreContext, SeoScoreContext} from "@/model/SeoScore";
    import ArticleKeywords from "@/components/article/ArticleKeywords.vue";
    import ArticleShareButton from "@/components/article/ArticleShareButton.vue";
    import {Article} from "@/model/Article";
    import {articleHasWritingContent} from "@/utils/Article";

    export default Vue.extend({
        name: "NavigationDrawer",
        components: {
            ArticleKeywords,
            ArticleShareButton,
            SeoScore, ListOfFeatures, LoginMenuTeaser, ArticleSimpleList, ArticleSaveButton,
            ArticlePreview, ArticleNewInspirationButton
        },
        props: {
            value: {
                type: Boolean,
                default: false
            },
            articleStep: {
                type: Number,
                default: null
            },
            seoScoreContext: {
                type: Object as PropType<SeoScoreContext>,
                default: () => seoScoreContext
            },
            disableSave: {
                type: Boolean,
                default: false
            }
        },
        computed: {
            Auth() {
                return Auth;
            },
            EventBus() {
                return EventBus;
            },
            isOwnerArticle(): boolean {
                return this.$route.name === 'Article' && this.$store.getters.authenticated;
            },
            isShareRoute(): boolean {
                return this.$route.name === 'SharedArticle';
            },
            isWritingStep(): boolean {
                const article = this.$store.getters.article as Article | undefined;
                // Keep the SEO pie on a writing article even while keyword
                // analysis tables are open. Never key this off keywordSelectionShown.
                if (article && String(article.selectedKeyword || '').trim()) {
                    return true;
                }
                if (article && articleHasWritingContent(article)) {
                    return true;
                }
                return Number(this.articleStep) === 3;
            },
            showSeoScore(): boolean {
                if (this.isShareRoute) {
                    return this.isWritingStep;
                }
                // Owner article page always uses the current pie menu, including
                // briefs that still wait for selectedKeyword (that used to look
                // like an older 3-step left menu next to newer articles).
                return this.isOwnerArticle;
            },
        },
        methods: {
            saveArticle: function (callback: (() => void) | null = null): void {
                EventBus.$emit('save', callback);
            },
            goRegister(): void {
                this.$store.commit('clearSharePreview');
                this.$router.push({name: 'Home'});
            },
        }
    });
</script>

<style lang="sass">
.navHeader .v-subheader
    margin-left: -16px

.mouse-cursor
    cursor: default

.pointer
    cursor: pointer !important

.share-drawer-cta
    width: 95%
    max-width: 95%
    margin: 16px auto 0
    padding: 12px
    border-radius: 12px
    background: rgba(8, 135, 220, 0.1)

.share-drawer-cta__text
    font-size: 12px
    line-height: 1.4
    margin: 0 0 10px
</style>
